import { ArrowRight, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const badges = [
  "ISO 27001 Certified",
  "AWS Partner",
  "Microsoft Gold",
];

export const Hero = () => {
  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center px-4 pt-24 pb-16 overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-sky-50/40 to-indigo-50/30 dark:from-slate-950 dark:via-slate-900 dark:to-slate-900" />

      {/* Grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.03] dark:opacity-[0.06]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%230EA5E9' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
        }}
      />

      {/* Glowing orbs */}
      <div className="absolute top-1/4 -left-20 w-96 h-96 bg-sky-400/20 dark:bg-sky-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-indigo-400/20 dark:bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto max-w-7xl relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left content */}
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-sky-500/10 border border-sky-500/20 text-sky-600 dark:text-sky-400 text-sm font-medium">
              <span className="w-2 h-2 rounded-full bg-sky-500 animate-pulse" />
              Trusted IT Partner Since 2009
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.08] tracking-tight text-slate-900 dark:text-white">
              Transform Your
              <br />
              Business With
              <br />
              <span className="bg-gradient-to-r from-sky-500 via-blue-500 to-indigo-500 bg-clip-text text-transparent">
                Smart IT
              </span>
            </h1>

            <p className="text-lg md:text-xl text-slate-600 dark:text-slate-400 max-w-lg leading-relaxed">
              We deliver comprehensive IT consulting, cloud infrastructure, and cybersecurity
              solutions that accelerate growth and reduce operational risk.
            </p>

            <div className="flex flex-wrap gap-4 pt-2">
              <Button
                size="lg"
                onClick={() => scrollTo("contact")}
                className="bg-sky-500 hover:bg-sky-600 text-white border-0 rounded-full px-8 h-12 text-base font-medium shadow-lg shadow-sky-500/25 hover:shadow-sky-500/40 transition-all hover:-translate-y-0.5"
              >
                Get Free Consultation
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => scrollTo("what-we-do")}
                className="border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-full px-8 h-12 text-base"
              >
                View Services
              </Button>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap items-center gap-4 pt-2">
              {badges.map((b) => (
                <div
                  key={b}
                  className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400"
                >
                  <CheckCircle className="h-4 w-4 text-sky-500 flex-shrink-0" />
                  {b}
                </div>
              ))}
            </div>
          </div>

          {/* Right: Dashboard visual */}
          <div className="hidden lg:block relative">
            <div className="relative">
              {/* Main card */}
              <div className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-2xl overflow-hidden">
                {/* Fake browser bar */}
                <div className="flex items-center gap-2 px-4 py-3 bg-slate-100 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                  </div>
                  <div className="flex-1 mx-4 bg-white dark:bg-slate-800 rounded-md px-3 py-1 text-xs text-slate-400 border border-slate-200 dark:border-slate-700">
                    luxe-tech.com/dashboard
                  </div>
                </div>
                <div className="p-6 space-y-5">
                  {/* Stats row */}
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      { label: "Uptime", value: "99.9%", color: "text-emerald-500" },
                      { label: "Response", value: "< 1hr", color: "text-sky-500" },
                      { label: "Clients", value: "500+", color: "text-indigo-500" },
                    ].map((s) => (
                      <div
                        key={s.label}
                        className="bg-slate-50 dark:bg-slate-900 rounded-xl p-4 text-center"
                      >
                        <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
                        <div className="text-xs text-slate-500 mt-1">{s.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Progress bars */}
                  <div className="space-y-3">
                    {[
                      { label: "Cloud Migration", pct: 92, color: "bg-sky-500" },
                      { label: "Security Score", pct: 97, color: "bg-emerald-500" },
                      { label: "Network Health", pct: 88, color: "bg-indigo-500" },
                    ].map((item) => (
                      <div key={item.label}>
                        <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400 mb-1">
                          <span>{item.label}</span>
                          <span className="font-medium">{item.pct}%</span>
                        </div>
                        <div className="h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${item.color} rounded-full`}
                            style={{ width: `${item.pct}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Alert */}
                  <div className="flex items-center gap-3 bg-emerald-50 dark:bg-emerald-500/10 rounded-xl p-3 border border-emerald-200 dark:border-emerald-500/20">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">All systems operational</p>
                      <p className="text-xs text-slate-500">Last checked 2 min ago</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating accent card */}
              <div className="absolute -top-6 -right-6 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-lg p-4 w-44">
                <div className="text-xs text-slate-500 mb-1">Incidents resolved</div>
                <div className="text-2xl font-bold text-slate-900 dark:text-white">1,240+</div>
                <div className="text-xs text-emerald-500 font-medium mt-1">↑ 12% this month</div>
              </div>

              {/* Floating accent card 2 */}
              <div className="absolute -bottom-6 -left-6 bg-sky-500 rounded-xl shadow-lg shadow-sky-500/30 p-4 w-40">
                <div className="text-xs text-sky-100 mb-1">Avg. satisfaction</div>
                <div className="text-2xl font-bold text-white">4.9 / 5</div>
                <div className="flex gap-0.5 mt-1">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-sky-200 text-sm">★</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
